/**
 * Composition rules for the phone bottom navigation bar. Pure and Angular-free
 * so the rules can be verified directly.
 */

/** The part of a navigation option these rules read. */
export interface FooterNavigationOption {
  id: string;
}

/**
 * Identifiers of the tools shown as direct destinations in the phone bottom
 * bar, in display order. They appear between the leading destinations (Home
 * and Calendar) and the trailing More destination.
 */
export const FOOTER_TOOL_OPTION_IDS: readonly string[] = [
  'contacts',
  'accounts',
];

/** The destinations of the phone bottom bar and the tools listed under More. */
export interface FooterNavigation<T extends FooterNavigationOption> {
  barOptions: T[];
  moreOptions: T[];
}

/**
 * Whether the identifier names a tool shown as a direct destination in the
 * phone bottom bar.
 *
 * @param id Navigation option identifier.
 */
export function isFooterToolOption(id: string | null | undefined): boolean {
  return !!id && FOOTER_TOOL_OPTION_IDS.includes(id);
}

/**
 * Compose the phone bottom bar and the More list from the tool menu options.
 *
 * A bar tool is shown only when the tool menu options contain it, so the bar
 * applies the same access rules as the tool menu. Every bar tool is removed
 * from the More list, and the More list keeps the order of the tool menu.
 *
 * @param leadingOptions Destinations placed before the bar tools.
 * @param trailingOptions Destinations placed after the bar tools.
 * @param menuOptions Tool menu options the user has access to; absent means none.
 * @returns The bar destinations in display order and the remaining More options.
 */
export function composeFooterNavigation<T extends FooterNavigationOption>(
  leadingOptions: readonly T[],
  trailingOptions: readonly T[],
  menuOptions: readonly T[] | null | undefined,
): FooterNavigation<T> {
  const options = (menuOptions ?? []).filter((option) => !!option);
  const barTools = FOOTER_TOOL_OPTION_IDS.map((id) =>
    options.find((option) => option.id === id),
  ).filter((option): option is T => !!option);
  return {
    barOptions: [...leadingOptions, ...barTools, ...trailingOptions],
    moreOptions: options.filter((option) => !isFooterToolOption(option.id)),
  };
}
